import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Case, GuidedLearningSession, GuidedLearningMessage, GuidanceResponse } from '../types';
import apiService from '../services/api';
import TimelineNavigation from '../components/GuidedLearning/TimelineNavigation';
import AnalyticsDashboard from '../components/GuidedLearning/AnalyticsDashboard';
import { 
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  BookmarkIcon,
  LightBulbIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const GuidedLearningPage: React.FC = () => {
  const { caseId, sessionId } = useParams<{ caseId?: string; sessionId?: string }>();
  const navigate = useNavigate();
  
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [session, setSession] = useState<GuidedLearningSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isGettingGuidance, setIsGettingGuidance] = useState(false);
  const [isCreatingCheckpoint, setIsCreatingCheckpoint] = useState(false);
  
  // Chat state
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingCharacter, setTypingCharacter] = useState<string | null>(null);
  const [pendingResponses, setPendingResponses] = useState<any[]>([]);
  
  // Guidance state
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidance, setGuidance] = useState<GuidanceResponse | null>(null);
  
  // Checkpoint state
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [checkpointDescription, setCheckpointDescription] = useState('');
  
  // Timeline navigation state
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineData, setTimelineData] = useState<any>(null);
  const [currentViewMode, setCurrentViewMode] = useState<'live' | 'timeline'>('live');
  const [isTimelinePlaying, setIsTimelinePlaying] = useState(false);
  
  // Analytics state
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    if (sessionId) {
      loadSessionData();
    } else if (caseId) {
      loadCaseData();
    }
  }, [caseId, sessionId]);

  const loadSessionData = async () => {
    try {
      setIsLoading(true);
      const sessionResponse = await apiService.getGuidedLearningSession(sessionId!);
      setSession(sessionResponse);
      
      // Load case data for display
      const caseResponse = await apiService.getCaseByCaseId(sessionResponse.caseId);
      setCaseData(caseResponse);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load session data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCaseData = async () => {
    try {
      setIsLoading(true);
      const caseResponse = await apiService.getCaseByCaseId(caseId!);
      setCaseData(caseResponse);
      
      // Check if there's an existing active session for this case
      const sessions = await apiService.getGuidedLearningSessions();
      const existingSession = sessions.find(s => 
        s.caseId === caseResponse.id && s.status === 'active'
      );
      
      if (existingSession) {
        setSession(existingSession);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load case data');
    } finally {
      setIsLoading(false);
    }
  };

  const createSession = async () => {
    if (!caseData) return;

    try {
      setIsCreatingSession(true);
      const newSession = await apiService.createGuidedLearningSession({
        caseId: caseData.caseId // Use caseId which contains the full instanceId
      });
      setSession(newSession);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create session');
    } finally {
      setIsCreatingSession(false);
    }
  };

  const sendMessage = async () => {
    if (!session || !newMessage.trim()) return;

    try {
      setIsSendingMessage(true);
      setNewMessage('');
      
      const response = await apiService.sendGuidedLearningMessage(session.id, {
        message: newMessage.trim()
      });

      // Update session with new messages
      const updatedSession = await apiService.getGuidedLearningSession(session.id);
      setSession(updatedSession);

      // Handle character responses with delays
      if (response.responses && response.responses.length > 0) {
        setPendingResponses(response.responses);
        setIsTyping(true);
        
        // Process responses with delays
        for (let i = 0; i < response.responses.length; i++) {
          const responseItem = response.responses[i];
          setTypingCharacter(responseItem.characterName);
          
          // Wait for the delay
          await new Promise(resolve => setTimeout(resolve, responseItem.delay));
          
          // Update session to show the new message
          const updatedSession = await apiService.getGuidedLearningSession(session.id);
          setSession(updatedSession);
          
          // Remove this response from pending
          setPendingResponses(prev => prev.slice(1));
        }
        
        setIsTyping(false);
        setTypingCharacter(null);
        setPendingResponses([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getGuidance = async () => {
    if (!session) return;

    try {
      setIsGettingGuidance(true);
      const guidanceResponse = await apiService.getGuidedLearningGuidance(session.id);
      setGuidance(guidanceResponse);
      setShowGuidance(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to get guidance');
    } finally {
      setIsGettingGuidance(false);
    }
  };

  const createCheckpoint = async () => {
    if (!session || !checkpointDescription.trim()) return;

    try {
      setIsCreatingCheckpoint(true);
      await apiService.createGuidedLearningCheckpoint(session.id, {
        description: checkpointDescription.trim()
      });
      
      // Update session
      const updatedSession = await apiService.getGuidedLearningSession(session.id);
      setSession(updatedSession);
      
      setCheckpointDescription('');
      setShowCheckpointModal(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create checkpoint');
    } finally {
      setIsCreatingCheckpoint(false);
    }
  };

  const endSession = async () => {
    if (!session) return;

    try {
      await apiService.updateGuidedLearningSessionStatus(session.id, {
        status: 'completed'
      });
      navigate('/cases');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to end session');
    }
  };

  const handleUseGuidanceSuggestion = (suggestion: any) => {
    setNewMessage(suggestion.text);
    setShowGuidance(false);
  };

  const loadTimelineData = async () => {
    if (!session) return;

    try {
      const timeline = await apiService.getGuidedLearningTimeline(session.id);
      setTimelineData(timeline);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load timeline data');
    }
  };

  const handleNavigateToPoint = (messageIndex: number) => {
    // Navigate to a specific point in the conversation
    setCurrentViewMode('timeline');
    // Here you would implement the logic to show messages up to that point
    console.log('Navigate to message index:', messageIndex);
  };

  const handleResumeFromPoint = async (messageIndex: number) => {
    if (!session) return;

    try {
      // Find the checkpoint at this message index
      const checkpoint = session.checkpoints?.find(cp => cp.messageIndex === messageIndex);
      if (checkpoint) {
        const updatedSession = await apiService.resumeGuidedLearningFromCheckpoint(session.id, checkpoint.id);
        setSession(updatedSession);
        setCurrentViewMode('live');
        setShowTimeline(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resume from checkpoint');
    }
  };

  const handleTimelinePlayPause = () => {
    setIsTimelinePlaying(!isTimelinePlaying);
    // Here you would implement timeline playback functionality
  };

  const toggleTimeline = async () => {
    if (!showTimeline) {
      await loadTimelineData();
    }
    setShowTimeline(!showTimeline);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/cases')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Cases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/cases')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Cases
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Guided Learning</h1>
            <p className="mt-2 text-gray-600">
              {caseData.title} - {caseData.patientProfile.name}, {caseData.patientProfile.age}
            </p>
            {session && (
              <p className="mt-1 text-sm text-gray-500">
                Session: {session.totalMessages} messages | Status: {session.status}
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            {!session ? (
              <button
                onClick={createSession}
                disabled={isCreatingSession}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {isCreatingSession ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <PlayIcon className="h-4 w-4 mr-2" />
                )}
                Start Learning Session
              </button>
            ) : (
              <>
                <button
                  onClick={toggleTimeline}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    showTimeline 
                      ? 'border-blue-300 text-blue-700 bg-blue-50' 
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Timeline
                </button>
                <button
                  onClick={() => setShowAnalytics(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  Analytics
                </button>
                <button
                  onClick={() => setShowCheckpointModal(true)}
                  disabled={isCreatingCheckpoint}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save Checkpoint
                </button>
                <button
                  onClick={endSession}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <StopIcon className="h-4 w-4 mr-2" />
                  End Session
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className={`space-y-8 ${showTimeline ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
          {/* Case Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Case Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Scenario</h3>
                <p className="text-gray-600">{caseData.scenario}</p>
              </div>
              
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                <h3 className="font-semibold text-amber-800 mb-2">Family Statement</h3>
                <p className="text-amber-700 italic">"{caseData.familyStatement}"</p>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-2">Learning Objectives</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                {caseData.clinicianObjectives.map((objective, index) => (
                  <li key={index}>{objective}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Chat Interface */}
          {session && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Conversation</h2>
                <div className="flex items-center space-x-2">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {session.sessionMetadata.characters.length} characters
                  </span>
                </div>
              </div>
              
              {/* Active Characters */}
              <div className="mb-4 flex flex-wrap gap-2">
                {session.sessionMetadata.characters.map((character) => (
                  <span
                    key={character.characterId}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {character.name}
                  </span>
                ))}
              </div>

              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {session.messages?.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'student' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender === 'student'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="text-xs font-medium mb-1">
                        {message.characterName || 'You'}
                      </div>
                      <div>{message.message}</div>
                    </div>
                  </div>
                ))}
                
                {/* Sending message indicator */}
                {isSendingMessage && (
                  <div className="flex justify-end">
                    <div className="bg-blue-100 text-blue-900 px-4 py-2 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="text-xs font-medium">Sending message...</div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Typing indicator */}
                {isTyping && typingCharacter && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                      <div className="text-xs font-medium mb-1">{typingCharacter} is typing...</div>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className={`flex space-x-2 transition-all duration-200 ${
                isSendingMessage ? 'animate-pulse' : ''
              }`}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder={isSendingMessage ? "Sending message..." : "Type your response..."}
                  className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    isSendingMessage 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300'
                  }`}
                  disabled={isSendingMessage || isTyping}
                />
                <button
                  onClick={sendMessage}
                  disabled={isSendingMessage || isTyping || !newMessage.trim()}
                  className={`px-4 py-2 rounded-md transition-all duration-200 ${
                    isSendingMessage 
                      ? 'bg-blue-500 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSendingMessage ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span className="text-sm">Sending...</span>
                    </div>
                  ) : (
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  )}
                </button>
              </div>

              {/* Guide Me Button - Moved here for better accessibility */}
              <div className="mt-3 flex justify-center">
                <button
                  onClick={getGuidance}
                  disabled={isGettingGuidance || isSendingMessage || isTyping}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isGettingGuidance ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  ) : (
                    <LightBulbIcon className="h-4 w-4 mr-2" />
                  )}
                  Guide Me
                </button>
              </div>
            </div>
          )}

          {/* Timeline Navigation */}
          {timelineData && (
            <TimelineNavigation
              navigationPoints={timelineData.navigationPoints}
              currentMessageIndex={session?.totalMessages || 0}
              onNavigateToPoint={handleNavigateToPoint}
              onResumeFromPoint={handleResumeFromPoint}
              isPlaying={isTimelinePlaying}
              onPlayPause={handleTimelinePlayPause}
              isOpen={showTimeline}
              onClose={() => setShowTimeline(false)}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Session Status */}
          {session && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Status</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900 capitalize">{session.status}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Messages</dt>
                  <dd className="text-sm text-gray-900">{session.totalMessages}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Checkpoints</dt>
                  <dd className="text-sm text-gray-900">{session.currentCheckpoint}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Started</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(session.createdAt).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Characters */}
          {session && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Characters</h3>
              <div className="space-y-3">
                {session.sessionMetadata.characters.map((character) => (
                  <div key={character.characterId} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <div className="font-medium text-gray-900">{character.name}</div>
                    <div className="text-sm text-gray-500">{character.role}</div>
                    <div className="text-xs text-gray-400">
                      {character.personality.emotionalState} • {character.personality.communicationStyle}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Checkpoints */}
          {session && session.checkpoints && session.checkpoints.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Checkpoints</h3>
              <div className="space-y-3">
                {session.checkpoints.map((checkpoint) => (
                  <div key={checkpoint.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <div className="font-medium text-gray-900">{checkpoint.description}</div>
                    <div className="text-sm text-gray-500">
                      Message {checkpoint.messageIndex} • {new Date(checkpoint.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Guidance Modal */}
      {showGuidance && guidance && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Guidance Suggestions</h3>
              <p className="text-sm text-gray-600 mb-4">{guidance.context}</p>
              
              <div className="space-y-3">
                {guidance.suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="font-medium text-gray-900 mb-2">{suggestion.text}</div>
                    <div className="text-sm text-gray-600 mb-2">{suggestion.description}</div>
                    <div className="text-xs text-gray-500 mb-3">{suggestion.reasoning}</div>
                    <button
                      onClick={() => handleUseGuidanceSuggestion(suggestion)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Use this suggestion
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowGuidance(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkpoint Modal */}
      {showCheckpointModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Checkpoint</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={checkpointDescription}
                  onChange={(e) => setCheckpointDescription(e.target.value)}
                  placeholder="e.g., After initial diagnosis discussion"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCheckpointModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={createCheckpoint}
                  disabled={isCreatingCheckpoint || !checkpointDescription.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isCreatingCheckpoint ? 'Creating...' : 'Create Checkpoint'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Dashboard */}
      {showAnalytics && session && (
        <AnalyticsDashboard
          sessionId={session.id}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default GuidedLearningPage;
